/**
 * React Query hooks for the Food Distribution module
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFDClients,
  getFDClient,
  updateFDClient,
  linkFDClient,
  getFDVisits,
  getFDVisit,
  updateFDVisit,
  getFDReferrals,
  updateFDReferral,
  getFDReportsSummary,
  getFDReportsOutcomes,
  getFDReportsProgress,
  getFDReportsServiceNeeds,
  getFDReportsExport,
} from '../services/foodDistributionService';
import {
  FDClientFilters,
  FDVisitFilters,
  FDReferralFilters,
  FDReportParams,
} from '../types/foodDistribution';

const STALE = 3 * 60 * 1000;  // 3 minutes
const GC    = 6 * 60 * 1000;  // 6 minutes

// ─── CLIENT HOOKS ─────────────────────────────────────────────────────────────

export function useFDClients(filters?: FDClientFilters) {
  return useQuery({
    queryKey: ['fd-clients', filters],
    queryFn: () => getFDClients(filters),
    staleTime: STALE,
    gcTime: GC,
    refetchOnWindowFocus: true,
  });
}

export function useFDClient(id: number | undefined) {
  return useQuery({
    queryKey: ['fd-client', id],
    queryFn: () => getFDClient(id!),
    enabled: !!id,
    staleTime: STALE,
    gcTime: GC,
  });
}

export function useUpdateFDClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateFDClient(id, data),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['fd-client', id] });
      queryClient.invalidateQueries({ queryKey: ['fd-clients'] });
    },
  });
}

export function useLinkFDClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, individualId }: { id: number; individualId: number }) =>
      linkFDClient(id, individualId),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['fd-client', id] });
    },
  });
}

// ─── VISIT HOOKS ──────────────────────────────────────────────────────────────

export function useFDVisits(clientId: number | undefined, filters?: FDVisitFilters) {
  return useQuery({
    queryKey: ['fd-visits', clientId, filters],
    queryFn: () => getFDVisits(clientId!, filters),
    enabled: !!clientId,
    staleTime: STALE,
    gcTime: GC,
  });
}

export function useFDVisit(id: number | undefined) {
  return useQuery({
    queryKey: ['fd-visit', id],
    queryFn: () => getFDVisit(id!),
    enabled: !!id,
    staleTime: STALE,
    gcTime: GC,
  });
}

export function useUpdateFDVisit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: { staffUser?: string; notes?: string } }) =>
      updateFDVisit(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fd-visits'] });
    },
  });
}

// ─── REFERRAL HOOKS ───────────────────────────────────────────────────────────

export function useFDReferrals(filters?: FDReferralFilters) {
  return useQuery({
    queryKey: ['fd-referrals', filters],
    queryFn: () => getFDReferrals(filters),
    staleTime: STALE,
    gcTime: GC,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateFDReferral() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateFDReferral(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fd-referrals'] });
    },
  });
}

// ─── REPORT HOOKS ─────────────────────────────────────────────────────────────

export function useFDReportsSummary(params?: FDReportParams) {
  return useQuery({
    queryKey: ['fd-reports-summary', params],
    queryFn: () => getFDReportsSummary(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useFDReportsOutcomes(params?: FDReportParams) {
  return useQuery({
    queryKey: ['fd-reports-outcomes', params],
    queryFn: () => getFDReportsOutcomes(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useFDReportsProgress(params?: FDReportParams) {
  return useQuery({
    queryKey: ['fd-reports-progress', params],
    queryFn: () => getFDReportsProgress(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useFDReportsServiceNeeds(params?: FDReportParams) {
  return useQuery({
    queryKey: ['fd-reports-service-needs', params],
    queryFn: () => getFDReportsServiceNeeds(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

export function useFDReportsExport(params?: FDReportParams, enabled = false) {
  return useQuery({
    queryKey: ['fd-reports-export', params],
    queryFn: () => getFDReportsExport(params),
    enabled,
    staleTime: 0, // always fresh for export
  });
}
